<script lang="ts">
  // UI components
  import { Badge } from '$lib/components/ui/badge/index.js';
  import { Button } from '$lib/components/ui/button';
  import * as Dialog from '$lib/components/ui/dialog/index.js';
  import { Input } from '$lib/components/ui/input/index.js';
  import { Label } from '$lib/components/ui/label/index.js';
  import { Minus, Plus } from '@lucide/svelte';
  // RoastLog
  import { displayTimeToSeconds, formatTime } from '$lib/RLogUtils';
  import { globalState } from '$lib/State.svelte';
  import type { NewRoastEvent, Nullable } from '$lib/types/RoastLog';
  import logger from '$lib/logger';

  let {
    open = $bindable(true),
    maxSeconds,
    roastEvent,
    onEventUpdated,
    onEventDeleted
  }: {
    open: boolean;
    maxSeconds: number;
    roastEvent: NewRoastEvent;
    onEventUpdated: CallableFunction;
    onEventDeleted: CallableFunction;
  } = $props();

  // Local variables for the debounce timers
  let holdTimeoutTimer: ReturnType<typeof setTimeout>;
  let intervalTimer: ReturnType<typeof setInterval>;
  let dirty = false;
  let formatError: Nullable<string> = $state(null);

  const editedDisplayTime: Nullable<string> = $derived.by(() => {
    if (roastEvent.time === null) {
      return '';
    }
    return formatTime(roastEvent.time);
  });

  const handleDelete = () => {
    onEventDeleted();
    open = false;
  };

  const handleSave = () => {
    if (!formatError) {
      open = false;
    }
  };

  class InvalidTemperatureError extends Error {
    constructor(message: string) {
      super(message);
      this.name = 'InvalidTemperatureError'; // Set the error name
    }
  }

  class TimeExceedsRoastError extends Error {
    constructor(roastTime: number) {
      super(`Time exceeds current roast time of ${formatTime(roastTime)}`);
      this.name = 'TimeExceedsRoastError';
    }
  }

  const updateTimeAndTemp = (newEventTime: number) => {
    const newTemperature = globalState.getBeanTempAtTime(newEventTime);
    if (!newTemperature) {
      logger.error(`Could not find temperature for time ${newEventTime}`);
      if (newEventTime > maxSeconds) {
        throw new TimeExceedsRoastError(maxSeconds);
      } else {
        throw new InvalidTemperatureError(`Could not find temperature at time ${newEventTime}`);
      }
    }

    formatError = null;

    roastEvent.time = newEventTime;
    roastEvent.temperature = newTemperature.toFixed(1);

    // If it's the first crack, reset the timer which will also update the development ratio
    if (roastEvent.isFirstCrack) {
      globalState.secondaryStartTime = newEventTime;
      logger.info('Updating first crack event');
    }
  };

  const incrementTime = () => {
    if (roastEvent.time < maxSeconds) {
      updateTimeAndTemp(roastEvent.time + 1);
    }
  };

  const decrementTime = () => {
    // the way the recording work, we don't have a reading at time = 0, so only decrement down to 1.
    if (roastEvent.time > 1) {
      updateTimeAndTemp(roastEvent.time - 1);
    }
  };

  const handlePointerDown = (operation: 'increment' | 'decrement') => {
    operation === 'increment' ? incrementTime() : decrementTime();

    dirty = true;

    // Set up the 1-second hold timeout
    holdTimeoutTimer = setTimeout(() => {
      // Start rapid increments after 1 second hold
      intervalTimer = setInterval(operation === 'increment' ? incrementTime : decrementTime, 100);
    }, 1000);
  };

  const handlePointerUp = () => {
    clearTimeout(holdTimeoutTimer);
    clearInterval(intervalTimer);
    if (dirty) {
      onEventUpdated(roastEvent.time);
      dirty = false;
    }
  };

  const handleDirectChange = (e: any) => {
    logger.info('Roasting event edit direct change', { value: e.target.value });

    formatError = null;

    try {
      const newEventTime = displayTimeToSeconds(e.target.value);
      updateTimeAndTemp(newEventTime);
      onEventUpdated(newEventTime);
      dirty = false;
    } catch (error) {
      if (error instanceof TimeExceedsRoastError) {
        logger.error(error.message);
        formatError = error.message;
      } else {
        logger.error(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
        formatError = 'Use MM:SS (eg. 2:23)';
      }
    }
  };
</script>

<Dialog.Root bind:open>
  <Dialog.Content class="sm:max-w-[425px]">
    <Dialog.Header>
      <Dialog.Title>Edit roasting event</Dialog.Title>
      <Dialog.Description>
        Make changes to the roasting event and Click save when you're done.
      </Dialog.Description>
    </Dialog.Header>
    <div class="grid gap-4 py-4">
      <div class="grid grid-cols-4 items-center gap-4">
        <Label for="event-time" class="text-right">Time</Label>
        <div class="col-span-3">
          <Input
            id="event-time"
            value={editedDisplayTime}
            onchange={handleDirectChange}
            type="text"
          />
          {#if formatError}
            <div class="mt-1 text-sm text-red-500">{formatError}</div>
          {/if}
        </div>

        <div class="col-span-4 flex justify-end gap-2">
          <Badge
            class="justify-center"
            onpointerdown={() => handlePointerDown('decrement')}
            onpointerup={handlePointerUp}
            onpointerleave={handlePointerUp}
          >
            <Minus size={16} />
          </Badge>
          <Badge
            class="justify-center"
            onpointerdown={() => handlePointerDown('increment')}
            onpointerup={handlePointerUp}
            onpointerleave={handlePointerUp}
          >
            <Plus size={16} />
          </Badge>
        </div>
      </div>
      <div class="grid grid-cols-4 items-center gap-4">
        <Label for="event-description" class="text-right">Description</Label>
        <Input
          id="event-description"
          bind:value={roastEvent.description}
          type="text"
          class="col-span-3"
        />
      </div>
    </div>
    <Dialog.Footer>
      <Button type="submit" onclick={handleDelete} variant="destructive">Delete</Button>
      <Button type="submit" onclick={handleSave}>Save changes</Button>
    </Dialog.Footer>
  </Dialog.Content>
</Dialog.Root>
